import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { ApprovalNotificationListing } from "api/notificationApprovalManagement";
import {
  deleteNotification,
  notificationStatusUpdate,
} from "api/notification";
import CreateNotification from "components/CreateNotification";
import NotificationDetailView from "components/NotificationDetailView";
import useDebounce from "hook/useDebounce";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ButtonComp from "themes/elements/Button";
import ModalComp from "themes/elements/Modal";
import TabComp from "themes/elements/Tab";
import TableComp from "themes/elements/Table";
import { retrieveUserData } from "utils/cryptoStorage";
import { getColumnConfig } from "./config";

function NotificationApprovalManagement() {
  const userToken = retrieveUserData();
  let admin_user_id;
  let admin_user_name;
  if (userToken) {
    ({ admin_user_id, admin_user_name } = userToken);
  }

  const [isCreate, setCreate] = useState(false);
  const [details, setDetails] = useState(false);
  const [currentPageNo, setCurrentPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [notifications, setNotifications] = useState([]);
  const [totalPageNo, setTotalPageNo] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [status, setStatus] = useState(1);
  const [tabActive, setTabActive] = useState("1");
  const [isEdit, setEdit] = useState(false);
  const [loadTable, setLoadTable] = useState(false);
  const [dlt, setDlt] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [viewStatus, setViewStatus] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectFormData, setRejectFormData] = useState({
    remark: "",
    allow_resubmission_flg: false,
  });
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [approveSchedule, setApproveSchedule] = useState(false);
  const [approveScheduleDate, setApproveScheduleDate] = useState(null);
  const [approveScheduleTime, setApproveScheduleTime] = useState(null);

  const [filterTitle, setFilterTitle] = useState("");
  const [filterAlert, setFilterAlert] = useState(null);
  const [filterExpiry, setFilterExpiry] = useState(null);
  const [filterType, setFilterType] = useState(null);

  const columnData = getColumnConfig(tabActive);

  const fetchNotifications = async () => {
    setIsLoading(true);
    const response = await ApprovalNotificationListing(
      currentPageNo,
      pageSize,
      status,
      admin_user_id,
      filterTitle,
      filterAlert,
      filterExpiry,
      filterType,
      null
    );
    if (response?.meta) {
      setTotalPageNo(response.meta.total || 0);
      setCurrentPageNo(response.meta.current_page || currentPageNo);
    }
    const formattedData = (response?.notification || []).map((item) => {
      const typeNum = Number(item?.notification_type);
      const isAlert = typeNum === 2;
      const typeLabel = isAlert ? "Alert" : "General";

      return {
      id: item?.notification_content_id,
      title: item?.notification_title,
      avatar: "svg/placeholder-for-avatar.svg",
      category: typeLabel,
      type: typeLabel,
      notification_type: Number.isFinite(typeNum) ? typeNum : undefined,
      author: item?.created_user_name ?? "",
      recipients: item?.wards?.length > 0 ? "Wards" : "All citizens",
      alertdate: item?.alert_date || null,
      status:
        item?.content_status_flg === 1
          ? "Pending"
          : item?.content_status_flg === 2
          ? "Approved"
          : item?.content_status_flg === 4
          ? "Published"
          : item?.content_status_flg === 5
          ? "Scheduled"
          : item?.content_status_flg === 6
          ? "Expired"
          : item?.content_status_flg === 3
          ? "Rejected"
          : "Draft",
      expirydate: item?.expiry_date || null,
      publisheddate: item?.alert_date || null,
      recipientsList: item?.wards?.length > 0 ? item.wards : "All citizens",
      content: item?.notification_content,
      alert_date_time: item?.alert_date_time,
      expiry_date_time: item?.expiry_date_time,
      published_date_time: item?.alert_date_time,
      wards: item?.wards,
      recipients_flg: item?.recipients_flg,
      status_flg: item?.content_status_flg,
      scheduled_flg: item?.scheduled_flg,
      start_date_time: item?.start_date_time,
      end_date_time: item?.end_date_time,
      activity: item?.activities,
      allow_resubmission_flg: item?.allow_resubmission_flg,
      created_user_name: item?.created_user_name,
      remarks: item?.approval_remarks,
      files: item?.files,
      destination_type: item?.redirection_type,
      destination:
        item?.redirection_type == null
          ? "None"
          : item?.redirection_type === 2
          ? "External"
          : item?.redirection_type === 0
          ? "None"
          : "In-app",
      external_link: item?.redirection_link,
      redirection_type: item?.redirection_type,
      display_us: item?.link_label,
    };
    });
    setNotifications(formattedData);
    setIsLoading(false);
  };

  const debouncedFetch = useDebounce(fetchNotifications, 300);

  useEffect(() => {
    debouncedFetch();
  }, [filterTitle]);

  useEffect(() => {
    const goHome = () => {
      setCreate(false);
      setDetails(false);
      setSelectedNotification(null);
      setEdit(false);
      setTabActive("1");
      setStatus(1);
      setRejectOpen(false);
      setDlt(false);
    };
    window.addEventListener("app:notification-approval-home", goHome);
    return () =>
      window.removeEventListener("app:notification-approval-home", goHome);
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [
    currentPageNo,
    pageSize,
    loadTable,
    status,
    filterAlert,
    filterExpiry,
    filterType,
    isCreate,
  ]);

  const handleTabChange = (key) => {
    setSelectedNotification(null);
    setDetails(false);
    setCreate(false);
    setEdit(false);
    setRejectOpen(false);
    setApproveSchedule(false);
    setApproveScheduleDate(null);
    setApproveScheduleTime(null);
    setCurrentPageNo(1);
    setSelectedData([]);
    setTabActive(key);
    setNotifications([]);
    if (key === "1") setStatus(1);
    else if (key === "2") setStatus(5);
    else if (key === "3") setStatus(4);
    else if (key === "4") setStatus(0);
    else if (key === "5") setStatus(3);
  };

  const handleEdit = (data) => {
    setSelectedNotification(data);
    setEdit(true);
    setCreate(true);
    setDetails(false);
  };

  const handleDelete = async (notificationId) => {
    setIsLoading(true);
    try {
      const response = await deleteNotification({
        notification_content_id: notificationId,
      });
      if (response?.message && response?.statusCode === 200) {
        toast.success(response.message);
      } else {
        toast.error(response?.message || "Delete failed");
      }
      setDlt(false);
      fetchNotifications();
    } catch (error) {
      toast.error(error?.message || "Delete failed");
      setDlt(false);
    }
    setIsLoading(false);
  };

  const dltModalFooter = (
    <div className="flex align-center justify-end gap-3">
      <ButtonComp onClick={() => setDlt(false)} variant="secondary">
        Cancel
      </ButtonComp>
      <ButtonComp
        variant="danger"
        loading={isLoading}
        onClick={() => handleDelete(selectedNotification?.id)}
      >
        Delete
      </ButtonComp>
    </div>
  );

  const openReadOnly = (data, statusOverride) => {
    setSelectedNotification(data);
    setViewStatus(statusOverride);
    setDetails(true);
  };

  const openDraftEditable = (data) => {
    setSelectedNotification(data);
    setEdit(true);
    setCreate(true);
    setDetails(false);
  };

  const commonTableProps = {
    column: columnData,
    data: notifications,
    totalPageNo,
    currentPageNo,
    setCurrentPageNo,
    pageSize,
    setPageSize,
    isLoading,
    selectedData,
    setSelectedData,
    tabActive,
    tabState: status,
    filterTitle,
    setFilterTitle,
    filterAlert,
    setFilterAlertDate: setFilterAlert,
    filterExpiry,
    setFilterExpiryDate: setFilterExpiry,
    filterType,
    setFilterType,
    handleEdit,
    onDeleteAction: (data) => {
      setSelectedNotification(data);
      setDlt(true);
    },
    copyAction: false,
  };

  const tabsData = [
    {
      key: "1",
      label: "Pending",
      children:
        tabActive === "1" ? (
          <TableComp
            {...commonTableProps}
            editAction={false}
            hideDeleteAction={true}
            dataClick={(data) => openReadOnly(data, 1)}
          />
        ) : null,
    },
    {
      key: "2",
      label: "Scheduled",
      children:
        tabActive === "2" ? (
          <TableComp
            {...commonTableProps}
            editAction={false}
            hideDeleteAction={true}
            dataClick={(data) => openReadOnly(data, 5)}
          />
        ) : null,
    },
    {
      key: "3",
      label: "History",
      children:
        tabActive === "3" ? (
          <TableComp
            {...commonTableProps}
            editAction={false}
            hideDeleteAction={true}
            dataClick={(data) => openReadOnly(data, 4)}
          />
        ) : null,
    },
    {
      key: "4",
      label: "Drafts",
      children:
        tabActive === "4" ? (
          <TableComp
            {...commonTableProps}
            editAction={true}
            hideDeleteAction={false}
            dataClick={(data) => openDraftEditable(data)}
          />
        ) : null,
    },
    {
      key: "5",
      label: "Rejected",
      children:
        tabActive === "5" ? (
          <TableComp
            {...commonTableProps}
            editAction={false}
            hideDeleteAction={true}
            dataClick={(data) => openReadOnly(data, 3)}
          />
        ) : null,
    },
  ];

  const handleApprove = async () => {
    setIsApproveLoading(true);
    try {
      const isScheduled = approveSchedule && approveScheduleDate && approveScheduleTime;
      const payload = {
        notification_content_id: selectedNotification?.id,
        status: isScheduled ? 5 : 4,
        modified_user_id: Number(admin_user_id),
        modified_user_name: admin_user_name,
        scheduled_flg: isScheduled ? 1 : 0,
      };
      if (isScheduled) {
        const parsed = dayjs(approveScheduleDate, "DD-MM-YYYY");
        if (parsed.isValid()) {
          payload.scheduled_date = parsed.format("YYYY-MM-DD") + " " + approveScheduleTime;
        }
      }
      const response = await notificationStatusUpdate(payload);
      if (response?.statusCode === 200) {
        toast.success(response?.message || "Notification approved");
        setDetails(false);
        setApproveSchedule(false);
        setApproveScheduleDate(null);
        setApproveScheduleTime(null);
        fetchNotifications();
      } else {
        toast.error(response?.message || "Approval failed");
      }
    } catch (err) {
      toast.error(err?.message || "Approval failed");
    }
    setIsApproveLoading(false);
  };

  const handleReject = async () => {
    setIsApproveLoading(true);
    try {
      const response = await notificationStatusUpdate({
        notification_content_id: selectedNotification?.id,
        status: 3,
        modified_user_id: Number(admin_user_id),
        modified_user_name: admin_user_name,
        remarks: rejectFormData.remark || "",
        allow_resubmission_flg: rejectFormData.allow_resubmission_flg ? 1 : 0,
      });
      if (response?.statusCode === 200) {
        toast.success(response?.message || "Notification rejected");
        setDetails(false);
        fetchNotifications();
      } else {
        toast.error(response?.message || "Rejection failed");
      }
    } catch (err) {
      toast.error(err?.message || "Rejection failed");
    }
    setIsApproveLoading(false);
  };

  return (
    <>
      {isCreate ? (
        <CreateNotification
          approval
          canPublishWithoutApproval={false}
          selectedNotificationDetails={selectedNotification}
          setOpen={setCreate}
          isEdit={isEdit}
          isDuplicate={false}
          setLoadTable={setLoadTable}
          loadNotifications={fetchNotifications}
          setDetails={setDetails}
        />
      ) : details && selectedNotification ? (
        viewStatus === 1 ? (
          <NotificationDetailView
            notification={selectedNotification}
            viewStatus={viewStatus}
            onCancel={() => {
              setDetails(false);
              setApproveSchedule(false);
              setApproveScheduleDate(null);
              setApproveScheduleTime(null);
            }}
            onApprove={handleApprove}
            onReject={() => setRejectOpen(true)}
            isLoading={isApproveLoading}
            rejectOpen={rejectOpen}
            setRejectOpen={setRejectOpen}
            rejectFormData={rejectFormData}
            setRejectFormData={setRejectFormData}
            handleReject={handleReject}
            approveSchedule={approveSchedule}
            setApproveSchedule={setApproveSchedule}
            approveScheduleDate={approveScheduleDate}
            setApproveScheduleDate={setApproveScheduleDate}
            approveScheduleTime={approveScheduleTime}
            setApproveScheduleTime={setApproveScheduleTime}
          />
        ) : (
          <NotificationDetailView
            notification={selectedNotification}
            viewStatus={viewStatus}
            onCancel={() => setDetails(false)}
            {...(viewStatus === 5
              ? {
                  onEdit: () => handleEdit(selectedNotification),
                  onDelete: () => setDlt(true),
                }
              : {})}
          />
        )
      ) : (
        <div className="page-wrapper flex flex-column gap-2">
          <div className="flex wrap gap-2 align-center justify-between">
            <h2 className="page-title">Notification Approval Management</h2>
            <ButtonComp
              onClick={() => {
                setSelectedNotification(null);
                setEdit(false);
                setCreate(true);
              }}
              prefix
            >
              Create notification
            </ButtonComp>
          </div>
          <TabComp
            isLoading={isLoading}
            tabActive={tabActive}
            handleTabChange={handleTabChange}
            exportData={selectedData}
            count={selectedData?.length}
            data={tabsData}
          />
        </div>
      )}
      <ModalComp
        footer={dltModalFooter}
        CancelClick={() => setDlt(false)}
        open={dlt}
        setOpen={setDlt}
        title="Delete Notification"
      >
        Are you sure you want to delete the notification titled &apos;{selectedNotification?.title}&apos;?
      </ModalComp>
    </>
  );
}

export default NotificationApprovalManagement;
